/**
 * V2-only setup completeness helpers (wizard + EconomicV2 badges).
 * Do not import from V1 screens.
 */
import {SetupInterface} from "../../interfaces/private/SetupInterface";
import {
    ALL_SYNC_MODULES,
    needsCommonSetup,
    normalizeEnabledModules,
    SyncModuleKey,
} from "../../features/public/SetupV2/helpers/onboardingModules";

export type SetupSectionKey = "invoice" | "payment" | "payout" | "subscription" | "refund" | "common";

/**
 * Single source of truth for V2 required setup fields.
 * Backend checkSetup (V2 branch) must stay aligned with this list.
 */
const REQUIRED: Record<SetupSectionKey, (keyof SetupInterface)[]> = {
    invoice: [
        "stripe_invoice_source",
        "economic_invoice_layout_number",
        "economic_invoice_payment_terms_number",
        "economic_invoice_booked_with",
        "economic_invoice_other_ref",
        "economic_invoice_vat_zone_domestic_number",
        "economic_invoice_vat_zone_eu_number",
        "economic_invoice_vat_zone_abroad_number",
        "economic_invoice_vat_zone_domestic_without_vat_number",
    ],
    payment: [
        "economic_payment_cashbook_number",
        "economic_payment_bank_account_number",
        "economic_payment_fee_account_number",
        "economic_payment_difference_currency_account_number",
    ],
    payout: [
        "economic_payout_cashbook_number",
        "economic_payout_bank_account_number",
        "economic_payout_difference_currency_account_number",
    ],
    subscription: [
        "economic_subscription_cashbook_number",
        "economic_subscription_balance_account_number",
        "economic_subscription_sales_account_number",
    ],
    // Credit notes reuse invoice layout / terms / VAT zones; booking is separate.
    refund: [
        "economic_invoice_layout_number",
        "economic_invoice_payment_terms_number",
        "economic_refund_booked_with",
        "economic_invoice_vat_zone_domestic_number",
        "economic_invoice_vat_zone_eu_number",
        "economic_invoice_vat_zone_abroad_number",
        "economic_invoice_vat_zone_domestic_without_vat_number",
    ],
    common: [
        "economic_customer_group_number",
        "economic_customer_payment_terms_number",
        "economic_product_group_number",
    ],
};

const isFilled = (value: unknown): boolean => {
    if (value === null || value === undefined || value === "") {
        return false;
    }
    return true;
};

export const usesCustomerBalanceLedger = (setup: SetupInterface): boolean =>
    setup.accounting_mode === "customer_balance_ledger";

export const getRequiredFieldsForSection = (
    section: SetupSectionKey,
    setup: SetupInterface
): (keyof SetupInterface)[] => {
    const isLedger = usesCustomerBalanceLedger(setup);

    // Invoice sync is skipped in ledger mode — do not block setup health.
    if (section === "invoice" && isLedger) {
        return [];
    }
    // Refund posts Sales/Customer journal (not credit notes) in ledger mode.
    if (section === "refund" && isLedger) {
        return [];
    }

    const fields = [...REQUIRED[section]];
    if (section === "payment") {
        if (isLedger) {
            fields.push("economic_payment_sales_account_number");
        } else if (!setup.economic_payment_matching_invoice) {
            fields.push("economic_payment_credit_account_number");
        }
    }
    if (section === "payout") {
        if (isLedger) {
            // Ledger: dedicated fee account required.
            fields.push("economic_payout_fee_account_number");
        } else {
            // Matching: credit required; fee optional (sync books net only when unset).
            fields.push("economic_payout_credit_account_number");
        }
    }
    return fields;
};

export const getMissingSetupFields = (
    section: SetupSectionKey,
    setup: SetupInterface
): (keyof SetupInterface)[] => {
    return getRequiredFieldsForSection(section, setup).filter((key) => {
        // Backend defaults null/empty refund booked-with to manual ("1").
        if (key === "economic_refund_booked_with" && !isFilled(setup[key])) {
            return false;
        }
        return !isFilled(setup[key]);
    });
};

export const isSetupSectionComplete = (
    section: SetupSectionKey,
    setup: SetupInterface
): boolean => getMissingSetupFields(section, setup).length === 0;

export const displaySetupValue = (
    value: unknown,
    emptyLabel: string
): string => {
    if (!isFilled(value)) {
        return emptyLabel;
    }
    return String(value);
};

/** Empty modules = all enabled (legacy V2 accounts before module picker). */
export const getEffectiveSyncModules = (setup: SetupInterface): SyncModuleKey[] => {
    const modules = normalizeEnabledModules(setup.enabled_sync_modules);
    return modules.length > 0 ? modules : [...ALL_SYNC_MODULES];
};

/**
 * Modules that should have a schedule / appear in "missing schedule" health.
 * Invoice is excluded in ledger mode (sync is skipped server-side).
 */
export const getSchedulableSyncModules = (setup: SetupInterface): SyncModuleKey[] => {
    const modules = getEffectiveSyncModules(setup);
    if (!usesCustomerBalanceLedger(setup)) {
        return modules;
    }

    return modules.filter((module) => module !== "invoice");
};

export const isSyncModuleEnabled = (
    setup: SetupInterface,
    module: SyncModuleKey
): boolean => getEffectiveSyncModules(setup).includes(module);

export const shouldShowCommonSetup = (setup: SetupInterface): boolean =>
    needsCommonSetup(getEffectiveSyncModules(setup));

export const getIncompleteEnabledSections = (
    setup: SetupInterface
): SetupSectionKey[] => {
    const modules = getEffectiveSyncModules(setup);
    const incomplete: SetupSectionKey[] = [];

    // Shared defaults first — modules depend on them when creating customers/products.
    if (needsCommonSetup(modules) && !isSetupSectionComplete("common", setup)) {
        incomplete.push("common");
    }

    const isLedger = usesCustomerBalanceLedger(setup);
    modules.forEach((section) => {
        // Invoice module stays listed but is not required while ledger is active.
        if (isLedger && section === "invoice") {
            return;
        }
        if (!isSetupSectionComplete(section, setup)) {
            incomplete.push(section);
        }
    });

    return incomplete;
};

export const findFirstIncompleteWizardStepIndex = (
    setup: SetupInterface,
    stepKeys: string[]
): number => {
    for (let i = 0; i < stepKeys.length; i++) {
        const key = stepKeys[i];
        if (key === "modules") {
            if (normalizeEnabledModules(setup.enabled_sync_modules).length === 0) {
                return i;
            }
            continue;
        }
        if (
            key === "invoice"
            || key === "payment"
            || key === "payout"
            || key === "subscription"
        ) {
            if (!isSetupSectionComplete(key, setup)) {
                return i;
            }
        }
    }
    return Math.max(0, stepKeys.length - 1);
};
